import { type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chip } from '../../components/ui/Chip';
import type { CampaignDetail } from '../../api/campaigns.api';

export type WizardStepId = 'basics' | 'triggers' | 'audience' | 'sequence' | 'settings' | 'review';

/** How a campaign decides who gets enrolled. Chosen on the Enrollment step.
 *  - `audience`: enroll a static audience snapshot at launch (classic).
 *  - `trigger`: auto-enroll contacts as JT events fire — the Audience step is
 *    skipped entirely for these campaigns. */
export type EnrollmentMode = 'audience' | 'trigger';

type StepDef = { id: WizardStepId; label: string };

// Canonical step order. `audience` is conditional — only present for campaigns
// enrolling by audience snapshot. See visibleSteps().
const ALL_STEPS: StepDef[] = [
  { id: 'basics', label: 'Basics' },
  { id: 'triggers', label: 'Enrollment' },
  { id: 'audience', label: 'Audience' },
  { id: 'sequence', label: 'Sequence' },
  { id: 'settings', label: 'Settings' },
  { id: 'review', label: 'Review and launch' },
];

/** Derive the enrollment mode from a persisted campaign. A campaign with a
 *  saved trigger_config enrolls by trigger; otherwise it enrolls by audience
 *  (the default for a fresh draft that hasn't chosen yet). */
export function enrollmentMode(campaign?: { trigger_config?: unknown } | null): EnrollmentMode {
  return campaign?.trigger_config ? 'trigger' : 'audience';
}

/** The steps shown for a given mode, renumbered 1..N. Trigger campaigns omit
 *  the Audience step. */
export function visibleSteps(mode: EnrollmentMode): { id: WizardStepId; label: string; index: number }[] {
  return ALL_STEPS
    .filter((s) => s.id !== 'audience' || mode === 'audience')
    .map((s, i) => ({ ...s, index: i + 1 }));
}

/** 1-based position + total for the FormHeader "Step X of Y" label. */
export function stepInfo(step: WizardStepId, mode: EnrollmentMode): { index: number; total: number } {
  const steps = visibleSteps(mode);
  const idx = steps.findIndex((s) => s.id === step);
  return { index: idx >= 0 ? idx + 1 : 1, total: steps.length };
}

// Back-compat alias — older imports use WIZARD_STEPS.length for the step count.
export const WIZARD_STEPS = visibleSteps('audience');

export function nextStep(current: WizardStepId, mode: EnrollmentMode = 'audience'): WizardStepId | null {
  const steps = visibleSteps(mode);
  const idx = steps.findIndex((s) => s.id === current);
  return idx >= 0 && idx < steps.length - 1 ? steps[idx + 1].id : null;
}

export function prevStep(current: WizardStepId, mode: EnrollmentMode = 'audience'): WizardStepId | null {
  const steps = visibleSteps(mode);
  const idx = steps.findIndex((s) => s.id === current);
  return idx > 0 ? steps[idx - 1].id : null;
}

export function stepPath(campaignId: string, step: WizardStepId): string {
  if (step === 'basics') return `/campaigns/${campaignId}/edit`;
  return `/campaigns/${campaignId}/${step}`;
}

/* ── Header used by every step ── */

export function WizardHeader({
  campaign,
  isNewDraft,
}: {
  campaign?: CampaignDetail | null;
  isNewDraft?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {isNewDraft ? 'New campaign' : campaign?.name || 'Untitled campaign'}
        </h1>
        <p
          style={{
            marginTop: 'var(--space-1)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--color-text-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          {isNewDraft ? (
            <>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--color-text-subtle)',
                }}
              />
              Draft · Not yet saved
            </>
          ) : campaign ? (
            <>
              <Chip variant={campaign.status === 'DRAFT' ? 'draft' : campaign.status === 'ACTIVE' ? 'live' : campaign.status === 'PAUSED' ? 'paused' : campaign.status === 'COMPLETED' ? 'completed' : 'archived'}>
                {campaign.status[0] + campaign.status.slice(1).toLowerCase()}
              </Chip>
              <span>Last updated {new Date(campaign.updated_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}

/* ── Breadcrumb ── */

export function WizardBreadcrumb({ stepLabel }: { stepLabel: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li>
          <Link to="/dashboard" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
            Engage
          </Link>
        </li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><ChevronRight /></li>
        <li>
          <Link to="/campaigns" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
            Campaigns
          </Link>
        </li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><ChevronRight /></li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
          {stepLabel}
        </li>
      </ol>
    </nav>
  );
}

/* ── Sidebar ── */

export function StepSidebar({
  activeStep,
  campaignId,
  mode = 'audience',
}: {
  activeStep: WizardStepId;
  campaignId?: string;
  mode?: EnrollmentMode;
}) {
  const steps = visibleSteps(mode);
  const activeIndex = steps.findIndex((s) => s.id === activeStep) + 1;
  const navigate = useNavigate();

  return (
    <aside
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--space-5) var(--space-4)',
      }}
    >
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {steps.map((step, i) => {
          const stepIndex = i + 1;
          const isActive = step.id === activeStep;
          const isDone = stepIndex < activeIndex;
          const isLast = i === steps.length - 1;
          const canNavigate = !!campaignId && (isDone || isActive);
          return (
            <li key={step.id} style={{ position: 'relative' }}>
              <div
                onClick={() => {
                  if (canNavigate && campaignId) navigate(stepPath(campaignId, step.id));
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-input)',
                  backgroundColor: isActive ? 'var(--color-sidebar-active-bg)' : 'transparent',
                  cursor: canNavigate ? 'pointer' : 'default',
                  position: 'relative',
                }}
              >
                <StepCircle step={stepIndex} state={isDone ? 'done' : isActive ? 'active' : 'pending'} />
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                    color: isActive
                      ? 'var(--color-sidebar-active-text)'
                      : isDone
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-muted)',
                  }}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: `calc(var(--space-3) + (var(--step-circle-size) / 2) - 0.5px)`,
                    top: `calc(var(--space-2) + var(--step-circle-size))`,
                    width: '1px',
                    height: `calc(var(--space-4) - var(--space-1))`,
                    backgroundColor: 'var(--color-border-default)',
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function StepCircle({ step, state }: { step: number; state: 'active' | 'done' | 'pending' }) {
  const bg =
    state === 'active'
      ? 'var(--step-circle-active-bg)'
      : state === 'done'
      ? 'var(--step-circle-done-bg)'
      : 'var(--step-circle-pending-bg)';
  const color = state === 'pending' ? 'var(--step-circle-pending-text)' : 'var(--step-circle-text)';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'var(--step-circle-size)',
        height: 'var(--step-circle-size)',
        borderRadius: 'var(--radius-full)',
        backgroundColor: bg,
        color,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        flexShrink: 0,
      }}
    >
      {state === 'done' ? <Check size={14} /> : step}
    </span>
  );
}

/* ── Form card wrapper ── */

export function FormCard({ children }: { children: ReactNode }) {
  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--space-8)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-6)',
      }}
    >
      {children}
    </section>
  );
}

export function FormHeader({ stepIndex, totalSteps, label }: { stepIndex: number; totalSteps: number; label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-brand-blue)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        Step {stepIndex} of {totalSteps}
      </span>
      <h2
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        {label}
      </h2>
    </div>
  );
}

/* ── Wizard layout ── */

export function WizardLayout({
  step,
  campaignId,
  campaign,
  modeOverride,
  children,
}: {
  step: WizardStepId;
  campaignId?: string;
  campaign?: CampaignDetail | null;
  /** Override the mode derived from the campaign — used by the Enrollment step
   *  so the sidebar reflects the live (unsaved) choice as the user toggles. */
  modeOverride?: EnrollmentMode;
  children: ReactNode;
}) {
  const mode = modeOverride ?? enrollmentMode(campaign);
  const meta = ALL_STEPS.find((s) => s.id === step)!;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <WizardBreadcrumb stepLabel={meta.label} />
      <WizardHeader campaign={campaign ?? undefined} isNewDraft={!campaignId} />
      <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <StepSidebar activeStep={step} campaignId={campaignId} mode={mode} />
        {children}
      </div>
    </div>
  );
}

/* ── Wizard footer (Back / Next / Save) ── */

export function WizardFooter({
  onBack,
  onNext,
  nextLabel = 'Next',
  isPending,
  helper,
  primaryDisabled,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  isPending?: boolean;
  helper?: ReactNode;
  primaryDisabled?: boolean;
}) {
  return (
    <div
      style={{
        marginTop: 'var(--space-2)',
        paddingTop: 'var(--space-5)',
        borderTop: '1px solid var(--color-border-default)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-4)',
      }}
    >
      <div>{helper}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <SecondaryButton onClick={onBack} disabled={!onBack || isPending}>
          <ArrowLeft />
          <span style={{ marginLeft: 'var(--space-1)' }}>Back</span>
        </SecondaryButton>
        <PrimaryButton onClick={onNext} disabled={!onNext || isPending || primaryDisabled}>
          {isPending ? 'Saving…' : nextLabel}
          <ArrowRight />
        </PrimaryButton>
      </div>
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color var(--transition-base)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color var(--transition-base)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

/* ── Icons ── */

export function Check({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function ChevronRight({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export function ArrowLeft({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

export function ArrowRight({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
